<template>
    <v-dialog v-model="dialogVisible" max-width="680" scrollable>
        <v-card>
            <v-card-title class="d-flex align-center pa-4">
                <v-icon class="mr-2" color="primary">mdi-update</v-icon>
                <span class="text-h6">{{ t('changelog.title') }}</span>
                <v-spacer />
                <v-btn icon="mdi-close" variant="text" density="compact" @click="dialogVisible = false" />
            </v-card-title>

            <v-divider />

            <v-card-text class="pa-0" style="max-height: 70vh;">
                <!-- Version selector tabs -->
                <v-tabs v-model="selectedVersionIndex" color="primary" density="compact" show-arrows>
                    <v-tab v-for="(entry, idx) in changelog" :key="entry.version" :value="idx">
                        v{{ entry.version }}
                        <v-chip v-if="idx === 0" size="x-small" color="primary" variant="flat" class="ml-2">
                            {{ t('changelog.latest') }}
                        </v-chip>
                    </v-tab>
                </v-tabs>

                <v-divider />

                <!-- Version content -->
                <div v-if="selectedEntry" class="pa-4">
                    <div class="d-flex align-center mb-4">
                        <span class="text-h6 font-weight-bold">v{{ selectedEntry.version }}</span>
                        <v-chip size="small" variant="outlined" class="ml-3">
                            {{ formatDate(selectedEntry.date) }}
                        </v-chip>
                    </div>

                    <!-- Features -->
                    <div v-if="selectedEntry.categories.features.length > 0" class="mb-4">
                        <div class="d-flex align-center mb-2">
                            <v-icon size="small" color="success" class="mr-2">mdi-star-circle</v-icon>
                            <span class="text-subtitle-1 font-weight-medium">{{ t('changelog.features') }}</span>
                        </div>
                        <v-list density="compact" class="ml-4 pa-0">
                            <v-list-item v-for="(item, i) in selectedEntry.categories.features" :key="'f-' + i"
                                         class="px-0 changelog-item">
                                <template #prepend>
                                    <v-icon size="x-small" color="success" class="mr-2">mdi-plus-circle-outline</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2 text-wrap">{{ item }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Bugfixes -->
                    <div v-if="selectedEntry.categories.bugfixes.length > 0" class="mb-4">
                        <div class="d-flex align-center mb-2">
                            <v-icon size="small" color="warning" class="mr-2">mdi-bug-check</v-icon>
                            <span class="text-subtitle-1 font-weight-medium">{{ t('changelog.bugfixes') }}</span>
                        </div>
                        <v-list density="compact" class="ml-4 pa-0">
                            <v-list-item v-for="(item, i) in selectedEntry.categories.bugfixes" :key="'b-' + i"
                                         class="px-0 changelog-item">
                                <template #prepend>
                                    <v-icon size="x-small" color="warning" class="mr-2">mdi-wrench-outline</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2 text-wrap">{{ item }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Announcements -->
                    <div v-if="selectedEntry.categories.announcements.length > 0" class="mb-4">
                        <div class="d-flex align-center mb-2">
                            <v-icon size="small" color="info" class="mr-2">mdi-bullhorn-outline</v-icon>
                            <span class="text-subtitle-1 font-weight-medium">{{ t('changelog.announcements') }}</span>
                        </div>
                        <v-list density="compact" class="ml-4 pa-0">
                            <v-list-item v-for="(item, i) in selectedEntry.categories.announcements" :key="'a-' + i"
                                         class="px-0 changelog-item">
                                <template #prepend>
                                    <v-icon size="x-small" color="info" class="mr-2">mdi-information-outline</v-icon>
                                </template>
                                <v-list-item-title class="text-body-2 text-wrap">{{ item }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Empty state -->
                    <div v-if="isEmptyVersion" class="text-center text-medium-emphasis py-4">
                        {{ t('changelog.noChanges') }}
                    </div>
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions class="pa-3">
                <v-spacer />
                <v-btn color="primary" variant="flat" @click="dialogVisible = false">
                    {{ t('changelog.close') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChangelogEntry } from '@/changelog/types'
import changelogData from '@/changelog/changelog.json'

const STORAGE_KEY = 'kmls_last_seen_version'

const { t } = useI18n()
const dialogVisible = ref(false)
const selectedVersionIndex = ref(0)
const changelog = ref<ChangelogEntry[]>(changelogData as ChangelogEntry[])

const selectedEntry = computed(() => changelog.value[selectedVersionIndex.value] ?? null)

const isEmptyVersion = computed(() => {
    if (!selectedEntry.value) return true
    const c = selectedEntry.value.categories
    return c.features.length === 0 && c.bugfixes.length === 0 && c.announcements.length === 0
})

const latestVersion = computed(() => changelog.value.length > 0 ? changelog.value[0].version : null)

function formatDate(dateStr: string): string {
    try {
        return new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric', month: 'long', day: 'numeric'
        })
    } catch {
        return dateStr
    }
}

/** Check if a new version is available and show dialog automatically */
function checkForNewVersion() {
    if (!latestVersion.value) return

    const lastSeen = localStorage.getItem(STORAGE_KEY)
    if (lastSeen !== latestVersion.value) {
        selectedVersionIndex.value = 0
        dialogVisible.value = true
    }
}

/** Mark current version as seen when closing */
watch(dialogVisible, (visible) => {
    if (!visible && latestVersion.value) {
        localStorage.setItem(STORAGE_KEY, latestVersion.value)
    }
})

/** Open dialog manually (e.g. from version link in sidebar) */
function open() {
    selectedVersionIndex.value = 0
    dialogVisible.value = true
}

onMounted(() => {
    // Defer to nextTick to avoid opening VDialog overlay during the mount flush cycle.
    // Opening a Teleport-based overlay while sibling layout components (VAppBar,
    // VNavigationDrawer, VMain) are still completing post-mount can cause
    // "Cannot read properties of null (reading 'parentNode')" in Vue's renderer.
    nextTick(() => {
        checkForNewVersion()
    })
})

defineExpose({ open })
</script>

<style scoped>
.changelog-item :deep(.v-list-item-title) {
    white-space: normal;
    line-height: 1.5;
}
</style>
